const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DAY = 24 * 60 * 60 * 1000;
const ATTRIBUTION_DAYS = 30;
const SESSION_IDLE_MS = 30 * 60 * 1000;

const uuid = () =>
  (window.crypto?.randomUUID?.() || `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`);

const store = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch { /* storage blocked */ }
};
const load = (key) => {
  try {
    return JSON.parse(localStorage.getItem(key));
  } catch {
    return null;
  }
};

export function getVisitorId() {
  let id = load("sc_vid");
  if (!id) {
    id = uuid();
    store("sc_vid", id);
  }
  return id;
}

export function getSessionId() {
  const now = Date.now();
  const s = load("sc_sid");
  if (s && now - s.last < SESSION_IDLE_MS) {
    store("sc_sid", { id: s.id, last: now });
    return s.id;
  }
  const id = uuid();
  store("sc_sid", { id, last: now });
  return id;
}

/** First-touch source for this visitor (kept 30 days): ref link + UTM + referrer. */
export function getSource() {
  const params = new URLSearchParams(window.location.search);
  const now = Date.now();
  const saved = load("sc_src");
  const fresh = saved && now - saved.at < ATTRIBUTION_DAYS * DAY ? saved : null;
  const ref = params.get("ref");
  const utm_source = params.get("utm_source") || params.get("s") || "";
  if (ref || utm_source || !fresh) {
    const next = {
      ref: ref || fresh?.ref || "",
      utm_source: utm_source || fresh?.utm_source || "",
      utm_medium: params.get("utm_medium") || fresh?.utm_medium || "",
      utm_campaign: params.get("utm_campaign") || fresh?.utm_campaign || "",
      referrer: document.referrer || fresh?.referrer || "",
      at: now,
    };
    store("sc_src", next);
    return next;
  }
  return fresh;
}

export const getRef = () => getSource().ref || "";

const isInternal = () => {
  try {
    const embedded = window.self !== window.top;
    return embedded || Boolean(localStorage.getItem("vsl_admin_key")) || window.location.pathname.startsWith("/builder");
  } catch {
    return true;
  }
};

export function trackEvent(type, extra = {}) {
  try {
    if (isInternal()) return;
    const src = getSource();
    const payload = JSON.stringify({
      type,
      path: window.location.pathname,
      ua: navigator.userAgent,
      vw: window.innerWidth,
      vh: window.innerHeight,
      visitor_id: getVisitorId(),
      session_id: getSessionId(),
      ref: src.ref,
      utm_source: src.utm_source,
      utm_medium: src.utm_medium,
      utm_campaign: src.utm_campaign,
      referrer: src.referrer,
      ...extra,
    });
    const blob = new Blob([payload], { type: "application/json" });
    if (!navigator.sendBeacon?.(`${API}/track`, blob)) {
      fetch(`${API}/track`, { method: "POST", body: payload, headers: { "Content-Type": "application/json" }, keepalive: true }).catch(() => {});
    }
  } catch { /* noop */ }
}

let lastPageviewAt = 0;

export function initTracker() {
  // Guard against React StrictMode double-mount / rapid re-inits producing duplicate pageviews.
  if (Date.now() - lastPageviewAt > 1500) {
    lastPageviewAt = Date.now();
    trackEvent("pageview");
  }

  const onClick = (e) => {
    const t = e.target instanceof Element ? e.target : null;
    if (!t) return;
    const el = t.closest("[data-testid]") || t.closest("a,button");
    if (!el) return;
    trackEvent("click", {
      element: el.dataset?.testid || el.tagName.toLowerCase(),
      text: (el.innerText || el.getAttribute("aria-label") || "").trim().slice(0, 60),
      x: Math.round((e.clientX / window.innerWidth) * 100),
      y: Math.round((e.clientY / window.innerHeight) * 100),
    });
  };
  document.addEventListener("click", onClick, { capture: true, passive: true });

  const formKey = `sc_form_${getSessionId()}`;
  const formTarget = document.querySelector("#apply");
  let observer = null;
  if (formTarget && "IntersectionObserver" in window && !sessionStorage.getItem(formKey)) {
    observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((en) => en.isIntersecting)) {
          sessionStorage.setItem(formKey, "1");
          trackEvent("form_view");
          observer.disconnect();
        }
      },
      { threshold: 0.25 }
    );
    observer.observe(formTarget);
  }

  return () => {
    document.removeEventListener("click", onClick, { capture: true });
    observer?.disconnect();
  };
}
